import { ReactNode } from "react";
import { matchPath, useLocation, useParams } from "react-router";
import { Role, RoleType } from "../models/Role";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import DashboardIcon from '@mui/icons-material/Dashboard';

export interface PageRoute {
    path: string;
    icon?: ReactNode;
    title: string;
    roles: RoleType[];
}

export interface HeaderRoute {
    mainRoute: PageRoute;
    subRoutes: PageRoute[];
    icon: ReactNode;
    title: string;
    roles: RoleType[];
    restoSpecific: boolean;
    isPremiumFeature?: boolean;
}

export function findMatchingRoute(path: string, routes: string[]) {
    return routes.find(route => matchPath(route, path) != null);
}

export function findMatchingPageRoute(path: string, pageRoutes: PageRoute[]) {
    return pageRoutes.find(route => matchPath(route.path, path) != null);
}

export function isRouteAccessible(route: PageRoute, userRoles: Role[]) {
    const roleTypes = userRoles.map(role => role.type);
    return route.roles.some(role => roleTypes.includes(role));
}

export function isHeaderAccessible(headerRoute: HeaderRoute, userRoles: Role[]) {
    if (headerRoute.roles.length === 0) return true;
    const roleTypes = userRoles.map(role => role.type);
    return headerRoute.roles.some(role => roleTypes.includes(role));
}

export const welcomePageRoute: PageRoute = {
    path: "/",
    title: "Home",
    roles: [RoleType.MENU_EDITOR, RoleType.OWNER]
};

export const loginPageRoute: PageRoute = {
    path: "/login/:lang",
    title: "navigation.login",
    roles: []
};

export const confirmSignupRoute: PageRoute = {
    path: "/confirm-signup/:userId/:token/:lang",
    title: "navigation.confirm_signup",
    roles: []
};

export const forgottenPasswordRoute: PageRoute = {
    path: "/forgotten-password/:lang",
    title: "navigation.forgotten_password",
    roles: []
};

export const resetPasswordRoute: PageRoute = {
    path: "/reset-password/:userId/:token/:lang",
    title: "navigation.reset_password",
    roles: []
};

export const signUpSuccessRoute: PageRoute = {
    path: "/signup-successful/:lang",
    title: "navigation.signup_successful",
    roles: []
};

export const menuEditorRoute: PageRoute = {
    path: "/:lang",
    title: "navigation.menu_editor",
    roles: []
}

export const selfSignupRoute: PageRoute = {
    path: "/sign-up/:lang",
    title: "navigation.signup",
    roles: []
};

export const subscriptionPageRoute: PageRoute = {
    path: "/subscribe/:lang",
    title: "navigation.subscribe",
    roles: [RoleType.OWNER]
}

export const paymentSettingsRoute: PageRoute = {
    path: "/payment-settings/:lang",
    title: "navigation.payment_settings",
    roles: [RoleType.OWNER]
}

export const userManagementRoute: PageRoute = {
    path: "/user-management/:lang",
    title: "navigation.user_management",
    roles: [RoleType.OWNER]
}

export const settingsPageRoute: PageRoute = {
    path: "/settings/:lang",
    title: "navigation.settings",
    roles: [RoleType.OWNER]
}

export const newRestaurantRoute: PageRoute = {
    path: "/create-restaurant/:lang",
    title: "navigation.create_restaurant",
    roles: []
}

export const privacyPolicyRoute: PageRoute = {
    path: "/privacy-policy/:lang",
    title: "navigation.privacy_policy",
    roles: []
}

export const termsAndConditionsRoute: PageRoute = {
    path: "/terms-and-conditions/:lang",
    title: "navigation.terms_and_conditions",
    roles: []
}

export const statisticsRoute: PageRoute = {
    path: "/statistics/:lang",
    title: "navigation.statistics",
    roles: [RoleType.OWNER]
}

export const mozrestRoute: PageRoute = {
    path: "/mozrest/:lang",
    title: "navigation.mozrest",
    roles: []
};

export const restrictedRoutes = [
    welcomePageRoute,
    menuEditorRoute,
    subscriptionPageRoute,
    paymentSettingsRoute,
    settingsPageRoute,
    newRestaurantRoute,
    mozrestRoute
];

export const headerRoutes: HeaderRoute[] = [
    {
        mainRoute: menuEditorRoute,
        subRoutes: [],
        icon: <MenuBookIcon />,
        title: "navigation.menu_editor",
        roles: [RoleType.MENU_EDITOR],
        restoSpecific: false
    },
    {
        mainRoute: statisticsRoute,
        subRoutes: [],
        icon: <AutoGraphIcon />,
        title: "navigation.statistics",
        roles: [RoleType.OWNER],
        restoSpecific: true,
        isPremiumFeature: true,
    },
    {
        mainRoute: mozrestRoute,
        subRoutes: [],
        icon: <DashboardIcon />,
        title: "navigation.mozrest",
        roles: [RoleType.MENU_EDITOR],
        isPremiumFeature: true,
        restoSpecific: true
    },
    {
        mainRoute: userManagementRoute,
        subRoutes: [],
        icon: <PeopleAltIcon />,
        title: "navigation.user_management",
        roles: [RoleType.OWNER],
        restoSpecific: true
    },
    {
        mainRoute: paymentSettingsRoute,
        subRoutes: [],
        icon: <ReceiptIcon />,
        title: "navigation.payment_settings",
        roles: [RoleType.OWNER],
        restoSpecific: true
    },
    {
        mainRoute: settingsPageRoute,
        subRoutes: [],
        icon: <SettingsIcon />,
        title: "navigation.settings",
        roles: [RoleType.OWNER],
        restoSpecific: true
    },
];

export const newRestaurantHeaderRoute: HeaderRoute = {
    mainRoute: newRestaurantRoute,
    subRoutes: [],
    icon: <AddBusinessIcon />,
    title: "navigation.new_restaurant",
    roles: [],
    restoSpecific: false
};

export const liveRoutes = [
    subscriptionPageRoute,
    menuEditorRoute,
    loginPageRoute,
    selfSignupRoute,
    paymentSettingsRoute,
    settingsPageRoute,
    userManagementRoute,
    userManagementRoute,
]

export function usePathReconstructor() {
    const params = useParams();
    const location = useLocation();
    const liveRouteStrings = liveRoutes.map(route => route.path);

    function replaceParam(propName: string, value: string) {
        let resultPath = findMatchingRoute(location.pathname, liveRouteStrings);
        if (resultPath == null) return null;
        for (const key in params) {
            if (key === propName) {
                resultPath = resultPath.replace(`:${key}`, value);
            } else if (params[key] != null) {
                resultPath = resultPath.replace(`:${key}`, params[key] as string);
            }
        }
        return resultPath;
    }

    function copyParamsFromPath(targetPath: string) {
        let resultPath = targetPath;
        for (const key in params) {
            resultPath = resultPath.replace(`:${key}`, params[key] as string);
        }
        return resultPath;
    }

    return {
        replaceParam,
        copyParamsFromPath,
    }
}

export const homePageRoute = process.env.REACT_APP_FRONTEND_URL + "/:lang"